import type { DirectMessagingParsedStructure } from '@dev-console/direct-messaging';

const HL7_NAMESPACE = 'urn:hl7-org:v3';

function childElements(element: Element): Element[] {
  return Array.from(element.childNodes).filter((node): node is Element => node.nodeType === 1);
}

function findHl7Child(element: Element, localName: string): Element | null {
  return (
    childElements(element).find((child) => child.namespaceURI === HL7_NAMESPACE && child.localName === localName) ??
    null
  );
}

function extractInformationRecipient(rootChildren: Element[]): string | null {
  for (const recipient of rootChildren.filter((child) => child.localName === 'informationRecipient')) {
    const intendedRecipient = findHl7Child(recipient, 'intendedRecipient');
    const telecom = intendedRecipient ? findHl7Child(intendedRecipient, 'telecom') : null;
    const value = telecom?.getAttribute('value')?.trim();
    if (value?.toLowerCase().startsWith('mailto:')) {
      return value.slice('mailto:'.length).trim() || null;
    }
  }
  return null;
}

function summarizeDocument(root: Element): DirectMessagingParsedStructure {
  const rootChildren = childElements(root).filter((child) => child.namespaceURI === HL7_NAMESPACE);
  const templateIds = rootChildren
    .filter((child) => child.localName === 'templateId')
    .map((templateId) => templateId.getAttribute('root'))
    .filter((templateId): templateId is string => Boolean(templateId));
  const sections = Array.from(root.getElementsByTagNameNS(HL7_NAMESPACE, 'section'));

  return {
    rootLocalName: root.localName,
    rootNamespace: root.namespaceURI,
    hl7RootChildNames: rootChildren.map((child) => child.localName),
    templateIds,
    hasNarrativeSection: sections.some((section) => findHl7Child(section, 'text') != null),
    informationRecipient: extractInformationRecipient(rootChildren),
  };
}

export function parseXmlInBrowser(xml: string): { document: Document | null; error: string | null } {
  if (!xml.trim()) return { document: null, error: 'XML is empty.' };
  if (/<!\s*(DOCTYPE|ENTITY)\b/i.test(xml)) {
    return { document: null, error: 'DTD and entity declarations are not allowed.' };
  }
  const document = new DOMParser().parseFromString(xml, 'application/xml');
  const parserError = document.getElementsByTagName('parsererror')[0];
  return parserError
    ? { document: null, error: parserError.textContent?.trim() || 'XML parsing failed.' }
    : { document, error: null };
}

export function browserXmlParser(xml: string): {
  error: string | null;
  structure: DirectMessagingParsedStructure | null;
} {
  const parsed = parseXmlInBrowser(xml);
  return {
    error: parsed.error,
    structure: parsed.document ? summarizeDocument(parsed.document.documentElement) : null,
  };
}

function formatElementOnlyContent(element: Element, depth: number): void {
  const childNodes = Array.from(element.childNodes);
  const containsText = childNodes.some(
    (node) => node.nodeType === Node.TEXT_NODE || node.nodeType === Node.CDATA_SECTION_NODE
  );
  if (containsText || childNodes.length === 0) return;

  for (const childNode of childNodes) {
    element.insertBefore(element.ownerDocument.createTextNode(`\n${'  '.repeat(depth + 1)}`), childNode);
    if (childNode instanceof Element) formatElementOnlyContent(childNode, depth + 1);
  }
  element.appendChild(element.ownerDocument.createTextNode(`\n${'  '.repeat(depth)}`));
}

export function formatXml(xml: string): string {
  const parsed = parseXmlInBrowser(xml);
  if (!parsed.document) throw new Error(parsed.error ?? 'XML parsing failed.');
  formatElementOnlyContent(parsed.document.documentElement, 0);

  const serializer = new XMLSerializer();
  const formattedDocument = Array.from(parsed.document.childNodes)
    .map((node) => serializer.serializeToString(node))
    .join('\n');
  const declaration = /^\s*(<\?xml\b[^?]*\?>)/i.exec(xml)?.[1];
  return declaration ? `${declaration}\n${formattedDocument}` : formattedDocument;
}

export async function sha256Hex(value: string): Promise<string> {
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}
